import React from 'react';
import { useTranslation } from '@/hooks/useTranslation';

type CanvasLoadingOverlayProps = {
  show: boolean;
  message?: string;
};

/**
 * Minimal overlay that blocks all interactions and shows
 * a bottom-centered pill with a status message.
 *
 * UX tokens follow docs/ux-rules.md:
 * - Primary Blue: #2E6BFF
 * - Elevation: overlay near top of the stack
 * - Rounded corners, readable contrast
 */
export const CanvasLoadingOverlay: React.FC<CanvasLoadingOverlayProps> = ({
  show,
  message,
}) => {
  const { t } = useTranslation('common');
  const displayMessage =
    message ??
    (t('status.canvasLoadingMessage', {
      defaultValue: 'Canvas is loading. Please wait.',
    }) as string);
  if (!show) return null;
  return (
    <>
      {/* Grey, semi-transparent blocker that captures events */}
      <div
        data-testid="tid.canvas.loading.blocker"
        className="fixed inset-0 z-[1000] bg-slate-900/35"
        aria-hidden
      />
      {/* Bottom-centered pill with message */}
      <div
        className="fixed bottom-6 left-1/2 z-[1000] -translate-x-1/2 rounded-[4px] bg-[#09436b] px-5 py-3 text-sm font-small text-white"
        role="status"
        aria-live="polite"
      >
        {displayMessage}
      </div>
    </>
  );
};

export default CanvasLoadingOverlay;
